import { useState } from 'react';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import LongChevronDown from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-down';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import useEventHandlers from '../OfferBottomSheet/useEventHandlers';
import OfferCard from '../OfferCard';

import type { Configs } from '@shared/types/configs';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './PartnersOffersCardsList.module.scss';

import {
  usePartnersOffersList,
  usePriceContext,
} from '@hotels-hotel-detail/data-access/hooks/useHotelOffersContext';
import {
  getEnhancedOffers,
  getEnhancedPartnersOffersList,
  getLowestPriceInRestOffers,
  getRemainingPartnerOffers,
} from '@hotels-hotel-detail/utils/multiple-offers';
import IntersectionObserverWrapper from '@shared/ui/common/IntersectionObserverWrapper';
import DebugToolViewer from '@shared/ui/components/DebugToolViewer';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import {
  ACTION_TYPE,
  CLICK_AREA,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoViewButtonClicked } from '@shared/utils-frontend/mini-events/hotels-action';
import { buildPriceBreakdownInfoForHD } from '@shared/utils-frontend/total-price-breakdown-service';
import stayLength from '@shared/utils-universal/guest-stay/stay-length';

type Props = {
  isMobile?: boolean;
  i18n: I18nService;
  miniEventTracker: MiniEventTracker;
  showTotalCost: boolean;
  configs: Configs;
};

const cls = cssModules(STYLES);

const PartnersOffersCardsList = ({
  configs: { enableMultiplePriceOnDetails },
  i18n,
  isMobile,
  miniEventTracker,
  showTotalCost,
}: Props) => {
  const [isExpanded, setIsExpended] = useState(false);

  const priceContext = usePriceContext();
  const partnersOffersList = usePartnersOffersList();
  const numberOfNights = stayLength({
    checkIn: priceContext.stay.checkIn,
    checkOut: priceContext.stay.checkOut,
  });

  const {
    culture: { currency },
    formatCurrency,
    translate,
  } = i18n;

  const enhancedOffers = getEnhancedOffers(partnersOffersList);
  const restPartnersOffersList = getRemainingPartnerOffers(
    enhancedOffers,
    partnersOffersList,
  );
  const showedPartnersOffersList = getEnhancedPartnersOffersList({
    partnersOffersList,
    isExpanded,
    enableMultiplePriceOnDetails,
    priceFilters: priceContext.filters,
    enhancedOffers,
    restPartnersOffersList,
  });

  const showMoreBtn = restPartnersOffersList.length > 0 && !isExpanded;

  let viewMoreLabel = translate('Button_viewMore');
  if (showMoreBtn) {
    const lowestPriceInRest = getLowestPriceInRestOffers(
      restPartnersOffersList,
    );

    viewMoreLabel = translate('Dayview_mobile_price_comparison_moreDeals', {
      price: lowestPriceInRest && formatCurrency(lowestPriceInRest),
    });
  }

  const {
    buildSeenEvent: onBuildPriceSeenEvent,
    trackSelectedEvent: onTrackPriceSelected,
  } = useEventHandlers(priceContext, currency, miniEventTracker, isMobile);

  const handleSeeMoreClick = () => {
    setIsExpended(!isExpanded);

    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.VIEW_BUTTON_CLICKED,
      buildAdditionalInfoViewButtonClicked(CLICK_AREA.DETAIL_VIEW_MORE),
    );
  };

  return (
    <div
      data-testid="partners-offers-cards-list"
      className={cls('PartnersOffersCardsList')}
    >
      {showedPartnersOffersList.map(
        ({ offer, offerPosition, partner, partnerRanking }, index) => (
          <IntersectionObserverWrapper
            key={offer.id}
            onElementSeen={() =>
              onBuildPriceSeenEvent({ offer, partner, partnerRanking })
            }
            ioWrapperClassName="PartnersOffersCardsList__card"
          >
            <>
              <OfferCard
                offer={offer}
                partner={partner}
                isMobile={!!isMobile}
                onClick={() =>
                  onTrackPriceSelected({
                    offer,
                    partner,
                    partnerRanking,
                    offerPosition,
                    position: index + 1,
                  })
                }
                costInfo={
                  showTotalCost
                    ? buildPriceBreakdownInfoForHD({
                        priceInfo: offer.price,
                        numberOfNights,
                        taxAndFeeInclusionPolicy:
                          priceContext.taxAndFeeInclusionPolicy,
                        priceType: priceContext.priceType,
                      })
                    : undefined
                }
                priceType={priceContext.priceType}
              />
              {offer.debugInfo && (
                <DebugToolViewer jsonString={offer.debugInfo} />
              )}
            </>
          </IntersectionObserverWrapper>
        ),
      )}
      {showMoreBtn && (
        <div className={cls('PartnersOffersCardsList__linkBtn')}>
          <BpkButtonV2
            type={BUTTON_TYPES.link}
            className={cls('PartnersOffersCardsList__textAndIcon')}
            onClick={() => handleSeeMoreClick()}
            data-testid="view-more-button"
          >
            <BpkText
              textStyle={TEXT_STYLES.label1}
              className={cls('PartnersOffersCardsList__text')}
            >
              {viewMoreLabel}
            </BpkText>
            <LongChevronDown />
          </BpkButtonV2>
        </div>
      )}
    </div>
  );
};

export default withConfiguration(
  withMiniEventTracker(withI18n(PartnersOffersCardsList)),
);
